import React, { useMemo, useState } from 'react';
import { Box, Button, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import PsychologyAltRoundedIcon from '@mui/icons-material/PsychologyAltRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import { useNavigate } from 'react-router-dom';
import { Assignment } from '../../types/interfaces';
import {
  getAssignmentInstructionLabel,
  getAssignmentTopic,
  getAssignmentTopics,
} from './assignmentTutorUtils';

type CoachMode = 'next' | 'deadline' | 'light';

interface StudyCoachCardProps {
  assignments: Assignment[];
  studentName?: string;
}

const StudyCoachCard: React.FC<StudyCoachCardProps> = ({ assignments, studentName }) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<CoachMode>('next');

  const sortedAssignments = useMemo(() => {
    return [...assignments].sort((left, right) => {
      const leftDate = left.dueDate ? new Date(left.dueDate).getTime() : Number.MAX_SAFE_INTEGER;
      const rightDate = right.dueDate ? new Date(right.dueDate).getTime() : Number.MAX_SAFE_INTEGER;
      return leftDate - rightDate;
    });
  }, [assignments]);

  const featuredAssignment = useMemo(() => {
    if (sortedAssignments.length === 0) {
      return null;
    }

    if (mode === 'deadline') {
      return sortedAssignments[0];
    }

    if (mode === 'light') {
      return [...sortedAssignments].sort((left, right) => {
        const leftOrder = left.exercise?.order || 0;
        const rightOrder = right.exercise?.order || 0;
        return leftOrder - rightOrder;
      })[0];
    }

    return sortedAssignments[0];
  }, [mode, sortedAssignments]);

  const topics = useMemo(() => getAssignmentTopics(assignments as any), [assignments]);

  const coachMessage = useMemo(() => {
    const firstName = studentName?.split(' ')[0] || 'kamu';

    if (!featuredAssignment) {
      return `${firstName}, taulu on juuri nyt ilahduttavan tyhjä. Ei avoimia assignmentteja, joten aivosi saavat pienen hengähdystauon.`;
    }

    const topic = getAssignmentTopic(featuredAssignment as any);
    const label = getAssignmentInstructionLabel(featuredAssignment as any);
    const dueDate = featuredAssignment.dueDate
      ? new Date(featuredAssignment.dueDate).toLocaleDateString('fi-FI')
      : null;

    if (mode === 'deadline') {
      return dueDate
        ? `Nopea tutkahavainto: lähin deadline hiipii vastaan tehtävässä "${label}". Aihe on ${topic}. Ei paniikkia, mutta tämä on fiksuin seuraava liike.`
        : `Deadline ei juuri nyt jahtaile sinua, mutta tehtävä "${label}" aiheen ${topic} suunnasta olisi hyväkin ottaa työn alle seuraavaksi.`;
    }

    if (mode === 'light') {
      return `Jos energia on tänään puoliksi koulun käytävälle unohtunut, aloita pehmeästi tehtävästä "${label}". Aihe: ${topic}. Yksi pieni askel on edelleen askel, ei mikään cutscene.`;
    }

    return `Hei ${firstName}, sinulla on ${assignments.length} avointa assignmenttia. Fiksuin seuraava siirto näyttäisi olevan "${label}" aiheesta ${topic}. Aloitetaanko siitä ilman turhaa boss fightia?`;
  }, [assignments.length, featuredAssignment, mode, studentName]);

  return (
    <Card
      elevation={3}
      sx={{
        mb: 3,
        overflow: 'hidden',
        borderRadius: 3,
        background: 'linear-gradient(135deg, #082f49 0%, #0f766e 52%, #f59e0b 140%)',
        color: 'white',
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
          <PsychologyAltRoundedIcon />
          <Typography variant="h5" fontWeight={700}>
            Tsemppi Coach
          </Typography>
        </Box>

        <Typography variant="body1" sx={{ maxWidth: 860, mb: 2, opacity: 0.96 }}>
          {coachMessage}
        </Typography>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" mb={2}>
          <Button
            variant={mode === 'next' ? 'contained' : 'outlined'}
            color="inherit"
            startIcon={<BoltRoundedIcon />}
            onClick={() => setMode('next')}
            sx={{ borderRadius: '999px' }}
          >
            Mitä teen seuraavaksi?
          </Button>
          <Button
            variant={mode === 'deadline' ? 'contained' : 'outlined'}
            color="inherit"
            startIcon={<EventAvailableRoundedIcon />}
            onClick={() => setMode('deadline')}
            sx={{ borderRadius: '999px' }}
          >
            Näytä lähin deadline
          </Button>
          <Button
            variant={mode === 'light' ? 'contained' : 'outlined'}
            color="inherit"
            startIcon={<PlayArrowRoundedIcon />}
            onClick={() => setMode('light')}
            sx={{ borderRadius: '999px' }}
          >
            Anna kevyt aloitus
          </Button>
        </Stack>

        {topics.length > 0 && (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" mb={2}>
            {topics.map((topic) => (
              <Chip
                key={topic}
                label={topic}
                size="small"
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.16)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.2)',
                }}
              />
            ))}
          </Stack>
        )}

        {featuredAssignment && (
          <Button
            variant="contained"
            color="warning"
            onClick={() => navigate(`/assignment/${featuredAssignment.id}`)}
            sx={{ borderRadius: 3 }}
          >
            Avaa suositeltu tehtävä
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default StudyCoachCard;